/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  Form,
  Input,
  Message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  TableColumnProps,
  Typography,
  Image,
  InputNumber
} from '@arco-design/web-react';
import './index.scss';
import { get, post } from '../requestNew';
import { FansTeamItem, FansTeamAddItem } from './types';
import dayjs from 'dayjs';

const FormItem = Form.Item;
const Option = Select.Option;

const formItemLayout = {
  labelCol: {
    span: 6
  },
  wrapperCol: {
    span: 16
  }
};

function FansTeam() {
  const [form] = Form.useForm<FansTeamAddItem>();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [sessionList, setSessionList] = useState<{ label: string; value: string }[]>([]);
  const [gameSession, setGameSession] = useState('');
  const [teamNameFilter, setTeamNameFilter] = useState('');
  const [modalTeamNameOptions, setModalTeamNameOptions] = useState<{ label: string; value: string; profilePic?: string }[]>([]);
  const [modalTeamNameMap, setModalTeamNameMap] = useState<Record<string, { name: string; pic: string; openId?: string }>>({});
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [dataList, setDataList] = useState<FansTeamItem[]>([]);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  // 表格列配置
  const columns: TableColumnProps[] = [
    {
      title: '序号',
      width: 80,
      render: (_, record, index) => (current - 1) * pageSize + index + 1
    },
    {
      title: '粉丝团标签',
      dataIndex: 'teamTag',
      width: 120
    },
    {
      title: '团长名',
      dataIndex: 'teamName',
      width: 150
    },
    {
      title: '粉丝团头像',
      dataIndex: 'profilePic',
      width: 100,
      render: (text) => {
        if (text && typeof text === 'string' && text.indexOf('http') === 0) {
          return (
            <Image
              src={text}
              width={50}
              height={50}
              preview={true}
              style={{ borderRadius: 4 }}
            />
          );
        }
        return '-';
      }
    },
    {
      title: '排序',
      dataIndex: 'orderNum',
      width: 80
    },
    {
      title: '粉丝团积分',
      dataIndex: 'teamScore',
      width: 120
    },
    {
      title: '总人数',
      dataIndex: 'teamNum',
      width: 100
    },
    {
      title: '首次报名人数',
      dataIndex: 'newUserNum',
      width: 140
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 180,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 150,
      render: (_, record: FansTeamItem) => (
        <>
          <Button className="btn" type="text" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="提示"
            content="确认删除?"
            onOk={() => handleDelete(record.id!)}
          >
            <Button className="btn" type="text" status="danger">
              删除
            </Button>
          </Popconfirm>
        </>
      )
    }
  ];

  // 获取赛季列表
  const getSessionList = () => {
    get('/gameConfig/getUmsGameConfigList').then((res: any) => {
      if (res && res.data) {
        const list = res.data.map((item: any) => {
          const { gameSession } = item;
          return { label: gameSession, value: gameSession };
        });
        setSessionList(list);
      }
    });
  };

  // 获取数据列表
  const getList = (reset = false, page = current, size = pageSize) => {
    if (reset) {
      setCurrent(1);
    }
    setLoading(true);

    const params: any = {
      pageNum: reset ? 1 : page,
      pageSize: size
    };

    if (gameSession) {
      params.gameSession = gameSession;
    }
    if (teamNameFilter) {
      params.teamName = teamNameFilter;
    }

    get('/gameTeam/list', params)
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          if (data && typeof data === 'object' && 'list' in data) {
            setDataList(data.list || []);
            setTotal(data.total || 0);
          } else {
            Message.error(message || '数据格式错误');
          }
        } else {
          Message.error(message || '获取数据失败');
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 新增
  const handleAdd = () => {
    setIsEdit(false);
    setEditId(null);
    form.resetFields();
    const defaultSession = gameSession || (sessionList.length > 0 ? sessionList[0].value : '');
    form.setFieldsValue({
      gameSession: defaultSession || undefined
    });

    setVisible(true);
  };

  // 编辑
  const handleEdit = (record: FansTeamItem) => {
    setIsEdit(true);
    setEditId(record.id || null);
    // 直接使用列表中的数据设置表单
    form.setFieldsValue(record);
    setVisible(true);
  };

  // 删除
  const handleDelete = (id: number) => {
    setLoading(true);
    post(`/gameTeam/delete?id=${id}`)
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          Message.success('删除成功！');
          getList(false, current, pageSize);
        } else {
          Message.error(message || '删除失败');
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 提交表单
  const handleSubmit = () => {
    form
      .validate()
      .then((values) => {
        setConfirmLoading(true);
        const url = isEdit ? '/gameTeam/update' : '/gameTeam/add';
        const data = isEdit ? { ...values, id: editId } : values;
        post(url, data)
          .then((res: any) => {
            const { code, message } = res || {};
            if (code === 200) {
              Message.success(isEdit ? '编辑成功！' : '新增成功！');
              handleCancel();
              getList();
            } else {
              Message.error(message || '操作失败');
            }
          })
          .catch(() => {
            Message.error('网络错误!');
          })
          .finally(() => {
            setConfirmLoading(false);
          });
      })
      .catch(() => {
        Message.error('请检查表单填写是否正确!');
      });
  };

  // 取消
  const handleCancel = () => {
    // 清除搜索定时器
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    setVisible(false);
    setConfirmLoading(false);
    form.resetFields();
    setIsEdit(false);
    setEditId(null);
    clearSearchOptions();
    setSearchLoading(false);
  };

  // 清空搜索选项的辅助函数
  const clearSearchOptions = () => {
    setModalTeamNameOptions([]);
    setModalTeamNameMap({});
  };

  // 搜索用户（输入时触发，带防抖）
  const handleTeamNameSearch = (keyword: string) => {
    // 清除之前的定时器
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    // 如果关键词为空，直接清空选项
    if (!keyword || keyword.trim() === '') {
      clearSearchOptions();
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);

    // 设置防抖定时器，300ms后执行搜索
    searchTimerRef.current = setTimeout(() => {
      get('/gameTeam/searchUser', { keyword: keyword.trim() })
        .then((res: any) => {
          if (res && res.code === 200 && res.data) {
            const userList = res.data || [];
            const options = userList.map((user: any) => ({
              label: user.gameName || '',
              value: user.gameName || '',
              profilePic: user.profilePic || ''
            }));
            setModalTeamNameOptions(options);
            
            // 构建映射表，用于级联填充头像和openId
            const map: Record<string, { name: string; pic: string; openId?: string }> = {};
            userList.forEach((user: any) => {
              if (user.gameName) {
                map[user.gameName] = {
                  name: user.gameName,
                  pic: user.profilePic || '',
                  openId: user.openId || ''
                };
              }
            });
            setModalTeamNameMap(map);
          } else {
            clearSearchOptions();
          }
        })
        .catch(() => {
          Message.error('搜索用户失败');
          clearSearchOptions();
        })
        .finally(() => {
          setSearchLoading(false);
        });
    }, 300);
  };

  // 处理粉丝团名称选择变化（级联填充头像和openId）
  const handleTeamNameChange = (value: string) => {
    form.setFieldValue('teamName', value);
    if (value && modalTeamNameMap[value]) {
      form.setFieldValue('profilePic', modalTeamNameMap[value].pic);
      form.setFieldValue('openId', modalTeamNameMap[value].openId);
    } else {
      form.setFieldValue('profilePic', undefined);
      form.setFieldValue('openId', undefined);
    }
  };

  // 处理弹框中赛季变化，更新粉丝团名称列表
  const handleModalSessionChange = (value: string) => {
    form.setFieldsValue({
      gameSession: value,
      teamName: undefined,
      profilePic: undefined,
      openId: undefined
    });
    clearSearchOptions();
  };

  // 处理赛季变化
  const handleSessionChange = (value: string) => {
    setGameSession(value);
    setCurrent(1);
  };

  // 查询
  const handleSearch = () => {
    getList(true, 1, pageSize);
  };

  // 重置
  const handleReset = () => {
    setTeamNameFilter('');
    if (sessionList.length > 0) {
      setGameSession(sessionList[0].value);
    }
    setCurrent(1);
    getList(true, 1, pageSize);
  };

  // 分页变化
  const handlePageChange = (page: number, size?: number) => {
    setCurrent(page);
    if (size && size !== pageSize) {
      setPageSize(size);
      getList(false, page, size);
    } else {
      getList(false, page, size || pageSize);
    }
  };

  // 初始化
  useEffect(() => {
    getSessionList();
  }, []);

  // 当赛季列表加载完成后，设置默认赛季并加载数据
  useEffect(() => {
    if (sessionList.length > 0 && !gameSession) {
      const defaultSession = sessionList[0].value;
      setGameSession(defaultSession);
    }
  }, [sessionList]);

  // 赛季变化时获取粉丝团列表
  useEffect(() => {
    if (gameSession) {
      getList(true, 1, pageSize);
    }
  }, [gameSession]);


  return (
    <div className="fans-team table-flex">
      <Space style={{ flexWrap: 'wrap', marginBottom: 16 }}>
        <div className="filter-item">
          <Typography.Text>团长名:</Typography.Text>
          <Input
            value={teamNameFilter}
            onChange={(value) => setTeamNameFilter(value)}
            style={{ width: 200 }}
            allowClear
            placeholder="请输入..."
          />
        </div>
        <div className="filter-item">
          <Typography.Text>赛季:</Typography.Text>
          <Select
            value={gameSession}
            onChange={handleSessionChange}
            style={{ width: 120 }}
            allowClear
            placeholder="请选择"
          >
            {sessionList.map(item => (
              <Option value={item.value} key={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </div>
        <Button
          className="filter-item"
          type="primary"
          onClick={handleSearch}
          loading={loading}
        >
          查询
        </Button>
        <Button
          className="filter-item"
          type="primary"
          onClick={handleAdd}
        >
          新建
        </Button>
      </Space>
      <div className="table-content">
        <Table
          loading={loading}
          columns={columns}
          data={dataList}
          pagination={{
            current,
            pageSize,
            total,
            sizeCanChange: true,
            sizeOptions: [10, 20, 50, 100],
            showTotal: true,
            onChange: handlePageChange,
            onPageSizeChange: (size: number) => handlePageChange(1, size)
          }}
          rowKey="id"
          className="table-scroll"
          scroll={{ y: '100%', x: '100%' }}
        />
      </div>
      <Modal
        title={isEdit ? '编辑粉丝团' : '新增粉丝团'}
        visible={visible}
        onOk={handleSubmit}
        confirmLoading={confirmLoading}
        onCancel={handleCancel}
        style={{ width: 600 }}
      >
        <Form {...formItemLayout} form={form}>
          <FormItem
            label="赛季"
            field="gameSession"
            rules={[{ required: true, message: '请选择粉丝团赛季' }]}
          >
            <Select
              placeholder="请选择"
              allowClear
              onChange={handleModalSessionChange}
            >
              {sessionList.map(item => (
                <Option value={item.value} key={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </FormItem>
          <FormItem
            label="团标签"
            field="teamTag"
            rules={[{ required: true, message: '请输入粉丝团标签' }]}
          >
            <Input placeholder="请输入..." />
          </FormItem>
          <FormItem
            label="团长名"
            field="teamName"
            rules={[{ required: true, message: '请选择粉丝团名称' }]}
          >
            <Select
              placeholder="请输入搜索..."
              allowClear
              showSearch
              loading={searchLoading}
              onSearch={handleTeamNameSearch}
              onChange={handleTeamNameChange}
              filterOption={false}
              notFoundContent={searchLoading ? '搜索中...' : '请输入关键词搜索用户'}
            >
              {modalTeamNameOptions.map(item => (
                <Option value={item.value} key={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </FormItem>
          <FormItem
            label="排序"
            field="orderNum"
            rules={[{ required: true, message: '请输入粉丝团排序' }]}
          >
            <InputNumber
              placeholder="请输入..."
              style={{ width: '100%' }}
              min={0}
            />
          </FormItem>
          <FormItem
            label="粉丝团头像"
            field="profilePic"
          >
            {(() => {
              const profilePic = form.getFieldValue('profilePic');
              return profilePic && typeof profilePic === 'string' ? (
                <Image
                  src={profilePic}
                  width={80}
                  height={80}
                  preview={true}
                  style={{ borderRadius: 4 }}
                />
              ) : (
                <Typography.Text type="secondary">请先选择粉丝团名称</Typography.Text>
              );
            })()}
          </FormItem>
          <FormItem
            field="openId"
            style={{ display: 'none' }}
          >
            <Input />
          </FormItem>
        </Form>
      </Modal>
    </div>
  );
}

export default FansTeam;
